<template>
  <ui-radiobutton
    :id="uniqueId"
    :name="name"
    :value="stringValue"
    :label="label"
    :model-value="modelValueString"
    :disabled="disabled"
    :required="required"
    @update:modelValue="handleModelUpdate"
    data-component="light-radio-wrapper"
  />
</template>

<script>
// Ensure the web component is registered
// import './plain/ui-radiobutton.js';

export default {
  name: 'RadioGroupWrapper',
  props: {
    modelValue: {
      type: [String, Boolean, Number],
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Boolean, Number],
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    bypassGuard: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
  },
  
  data() {
    return {
      // Generate a unique ID if not provided
      uniqueId: this.id || `radio-${Math.random().toString(36).substring(2, 10)}`,
      // Track when the component last processed a user interaction
      lastInteraction: null,
      // For debugging in production
      debug: window.location.search?.includes('debug=true')
    };
  },
  
  computed: {
    // Convert model value to string for the web component
    modelValueString() {
      return String(this.modelValue);
    },
    
    // Convert value to string for the web component
    stringValue() {
      return String(this.value);
    },
  },
  
  emits: ['update:modelValue'],
  
  methods: {
    log(...args) {
      if (this.debug) {
        console.log(`[LightRadioWrapper:${this.uniqueId}]`, ...args);
      }
    },
    
    /**
     * Handle model value updates from the web component
     */
    handleModelUpdate(event) {
      this.lastInteraction = Date.now();
      
      let newValue = event;
      
      // Handle CustomEvent with detail property
      if (event && event.detail !== undefined) {
        newValue = event.detail;
      }
      
      this.log('Handling model update:', { 
        newValue, 
        originalType: typeof this.value
      });
      
      // Convert value back to the appropriate type based on props
      if (typeof this.value === 'boolean') {
        newValue = newValue === 'true';
      } else if (typeof this.value === 'number') {
        newValue = Number(newValue);
      }
      
      this.log('Emitting update:modelValue:', { 
        newValue, 
        convertedType: typeof newValue
      });
      
      this.$emit('update:modelValue', newValue);
    },
    
    /**
     * Force update of other radio buttons
     */
    updateOtherRadios() {
      // Force an explicit event at the document level for maximum compatibility
      if (this.modelValue === this.value) {
        try {
          document.dispatchEvent(
            new CustomEvent('radio-group-change', {
              detail: {
                name: this.name,
                value: this.stringValue,
                source: this.$el,
                timestamp: Date.now()
              },
              bubbles: true,
              composed: true
            })
          );
          this.log('Forcibly dispatched radio-group-change');
        } catch (e) {
          console.error('Error dispatching global event', e);
        }
      }
    }
  },
  
  mounted() {
    this.log('Component mounted', {
      modelValue: this.modelValue,
      value: this.value,
      name: this.name
    });
    
    // Set bypass guard attribute if specified
    if (this.bypassGuard) {
      this.$el.setAttribute('data-bypass-guard', '');
    }
    
    // Make clicking the label work by attaching an ID
    if (this.$el && !this.$el.id && this.uniqueId) {
      this.$el.id = this.uniqueId;
    }
    
    // Force a sync of the current state for this component
    this.$nextTick(() => {
      this.updateOtherRadios();
    });
  },
  
  updated() {
    this.log('Component updated', {
      modelValue: this.modelValue,
      value: this.value
    });
    
    // Re-apply bypass guard if needed
    if (this.bypassGuard) {
      this.$el.setAttribute('data-bypass-guard', '');
    } else {
      this.$el.removeAttribute('data-bypass-guard');
    }
  },
  
  // Force an update of check state when the model changes
  watch: {
    modelValue: {
      handler(newVal) {
        this.log('Model value changed:', { 
          newVal, 
          value: this.value,
          matches: String(newVal) === String(this.value)
        });
        
        // Force a sync of the current state for this component
        this.$nextTick(() => {
          if (String(newVal) === String(this.value)) {
            this.$el.setAttribute('checked', '');
          } else {
            this.$el.removeAttribute('checked');
          }
          
          this.updateOtherRadios();
        });
      },
      immediate: true
    }
  }
};
</script>